<template>
  <main class="engraving-page">
    <div class="engraving-container">
      <div class="breadcrumb"><router-link to="/index">首页</router-link><span>/</span><span>雕刻机</span></div>

      <section id="overview" class="product-hero" aria-labelledby="product-title">
        <div class="hero-copy">
          <p class="eyebrow">JUITA 川田智能科技 · 雕刻机系列</p>
          <h1 id="product-title">垂直高速服装<br />模板雕刻机</h1>
          <p class="hero-description">伺服精驱，钻铣、切割、绘图一体化。<br />为服装与皮具行业打造高效智能加工体验。</p>
          <div class="model-tags"><span>JT-1512D</span><span>JT-1812D</span></div>
          <div class="hero-actions">
            <button type="button" class="primary-button" @click="scrollToSection('specifications')">查看技术参数 <span aria-hidden="true">↓</span></button>
            <button type="button" class="secondary-button" @click="scrollToSection('resources')">产品资料</button>
          </div>
          <div class="hero-highlights"><span>XYZ 伺服电机</span><span>自动吸尘</span><span>AI 模板软件</span></div>
        </div>
        <div class="hero-visual">
          <img :src="machineImage" alt="川田 JT-1512D / JT-1812D 垂直高速服装模板雕刻机" width="1600" height="1046" />
          <div class="visual-caption"><span class="caption-line"></span>服装 / 皮具 · 全环节加工</div>
        </div>
      </section>

      <nav class="section-nav" aria-label="雕刻机页面导航">
        <button v-for="section in sections" :key="section.id" type="button" :class="{ active: activeSection === section.id, 'video-nav-button': section.id === 'videos' }" :aria-current="activeSection === section.id ? 'location' : null" @click="scrollToSection(section.id)">{{ section.label }}</button>
      </nav>

      <section id="features" class="content-section" aria-labelledby="features-title">
        <div class="section-heading"><div><p class="eyebrow">FUNCTION OVERVIEW</p><h2 id="features-title">精密加工，从每个细节开始</h2></div><p>精准、高效、洁净，让模板制作更从容。</p></div>
        <div class="feature-grid">
          <article v-for="(feature, index) in features" :key="feature.title" class="feature-card">
            <img :src="feature.image" :alt="feature.title" width="357" height="177" loading="lazy" />
            <div class="feature-copy"><span class="feature-number">0{{ index + 1 }}</span><h3>{{ feature.title }}</h3><p>{{ feature.description }}</p></div>
          </article>
        </div>
        <div class="additional-features">
          <div v-for="item in additionalFeatures" :key="item.title"><span aria-hidden="true">✓</span><div><h3>{{ item.title }}</h3><p>{{ item.description }}</p></div></div>
        </div>
      </section>

      <section id="applications" class="content-section" aria-labelledby="applications-title">
        <div class="section-heading"><div><p class="eyebrow">APPLICATION RANGE</p><h2 id="applications-title">一台设备，覆盖多种应用</h2></div><p>从纸样切割到模板铣刻，衔接服装与皮具加工环节。</p></div>
        <div class="application-grid">
          <article v-for="(application, index) in applications" :key="application.title" class="application-card">
            <img :src="application.image" :alt="application.title + '应用示例'" width="357" height="143" loading="lazy" />
            <div><span>{{ String(index + 1).padStart(2, '0') }}</span><h3>{{ application.title }}</h3></div>
          </article>
        </div>
      </section>

      <section id="software" class="content-section software-section" aria-labelledby="software-title">
        <div class="section-heading"><div><p class="eyebrow">AI TEMPLATE DESIGN SOFTWARE</p><h2 id="software-title">标配川田 AI 模板设计软件</h2></div><span class="standard-badge">两款型号均标配</span></div>
        <div class="software-layout">
          <figure class="software-visual"><img :src="softwareImage" alt="川田 AI 模板设计软件界面，展示模板图形与设计工具" width="361" height="286" loading="lazy" /><figcaption>川田 AI 模板设计软件界面</figcaption></figure>
          <div class="software-features">
            <article v-for="(item, index) in softwareFeatures" :key="item.title"><span class="software-number">0{{ index + 1 }}</span><div><h3>{{ item.title }}</h3><p>{{ item.description }}</p></div></article>
          </div>
        </div>
      </section>

      <section id="specifications" class="content-section" aria-labelledby="specifications-title">
        <div class="section-heading"><div><p class="eyebrow">TECHNICAL SPECIFICATIONS</p><h2 id="specifications-title">选择适合您的工作幅面</h2></div><p>两款机型，工作范围支持定制。</p></div>
        <div class="model-grid">
          <article v-for="model in models" :key="model.name" class="model-card"><p>{{ model.name }}</p><h3>{{ model.range }} <span>cm</span></h3><div>工作范围 · 支持定制</div><span class="model-footprint">占地面积：{{ model.footprint }}</span></article>
        </div>
        <div class="table-scroll" role="region" aria-label="雕刻机型号参数对比，可横向滚动" tabindex="0">
          <table class="specification-table">
            <caption class="sr-only">JT-1512D 与 JT-1812D 技术参数对比</caption>
            <thead><tr><th scope="col">产品参数</th><th scope="col">JT-1512D</th><th scope="col">JT-1812D</th></tr></thead>
            <tbody><tr v-for="row in specifications" :key="row.label"><th scope="row">{{ row.label }}</th><td v-if="row.values" v-for="(value, index) in row.values" :key="index">{{ value }}</td><td v-if="!row.values" colspan="2">{{ row.value }}</td></tr></tbody>
          </table>
        </div>
        <p class="specification-note">参数整理自产品宣传资料。原图的主轴转速及绘图、铣板、切纸速度单位未完整标明，具体数值请查看原始参数表并向厂商确认。</p>
        <button type="button" class="text-button" @click="openBrochure(brochures[3])">查看原始参数表 <span aria-hidden="true">↗</span></button>
      </section>

      <section id="resources" class="content-section" aria-labelledby="resources-title">
        <div class="section-heading"><div><p class="eyebrow">PRODUCT RESOURCES</p><h2 id="resources-title">产品资料</h2></div><p>查看完整宣传页，了解更多产品细节。</p></div>
        <div class="resource-grid">
          <article v-for="brochure in brochures" :key="brochure.file" class="resource-card"><button type="button" class="brochure-preview" @click="openBrochure(brochure)"><img :src="brochure.url" :alt="brochure.title + '宣传页，点击放大查看'" width="1280" height="1739" loading="lazy" /><span>放大查看 <span aria-hidden="true">↗</span></span></button><div class="resource-copy"><h3>{{ brochure.title }}</h3><a :href="brochure.url" :download="brochure.file">下载原图 <span aria-hidden="true">↓</span></a></div></article>
        </div>
        <aside class="contact-panel"><div><p class="eyebrow">JUITA · 浙江川田智能科技有限公司</p><h3>了解雕刻机产品与定制方案</h3><p>地址：浙江省台州市台州湾新区聚英路 2077 号</p></div><div class="contact-links"><a href="tel:4001813888"><span>销售热线</span><strong>400-1813-888</strong></a><a href="tel:4001872628"><span>售后服务热线</span><strong>400-1872-628</strong></a></div></aside>
      </section>

      <section id="videos" class="content-section" aria-labelledby="videos-title" :aria-busy="videoLoading">
        <div class="section-heading">
          <div><p class="eyebrow">VIDEO TUTORIALS</p><h2 id="videos-title">雕刻机视频教程</h2></div>
          <p>设备安装与软件使用，跟随视频了解操作流程。</p>
        </div>
        <div v-if="videoLoading" class="video-status" role="status">正在加载视频...</div>
        <div v-else-if="videoError" class="video-status" role="alert">
          <p>{{ videoError }}</p>
          <button type="button" class="secondary-button" @click="fetchVideos()">重新加载</button>
        </div>
        <div v-else-if="videos.length" class="video-grid">
          <router-link v-for="video in videos" :key="video.id" class="video-card" :to="{ path: '/vidCont', query: { id: video.id } }" :aria-label="'观看视频：' + video.name">
            <div class="video-cover">
              <span class="video-cover-placeholder" aria-hidden="true">JUITA</span>
              <img v-if="video.image && !video.coverFailed" :src="videoCoverUrl(video.image)" :alt="video.name" loading="lazy" @error="handleVideoCoverError(video)" />
              <span class="video-play" aria-hidden="true">▶</span>
            </div>
            <div class="video-card-body"><h3 :title="video.name">{{ video.name }}</h3><span>点击观看 <span aria-hidden="true">→</span></span></div>
          </router-link>
        </div>
        <div v-else class="video-status" role="status">暂无相关视频</div>
        <div v-if="videoTotal > videoPage.pageSize && !videoError" class="video-pagination">
          <a-pagination :current="videoPage.pageNo" :page-size="videoPage.pageSize" :total="videoTotal" :show-size-changer="false" :disabled="videoLoading" @change="changeVideoPage" />
        </div>
      </section>
    </div>

    <div v-if="previewBrochure" class="brochure-overlay" @click.self="closePreview" @keydown.esc="closePreview">
      <section ref="previewDialog" class="brochure-dialog" role="dialog" aria-modal="true" :aria-label="previewBrochure.title" tabindex="-1">
        <div class="dialog-toolbar"><h2>{{ previewBrochure.title }}</h2><div><a :href="previewBrochure.url" :download="previewBrochure.file">下载原图</a><button ref="closeButton" type="button" aria-label="关闭资料预览" @click="closePreview">×</button></div></div>
        <div class="dialog-image"><img :src="previewBrochure.url" :alt="previewBrochure.title + '完整宣传资料'" /></div>
      </section>
    </div>
  </main>
</template>

<script>
import machineImage from "../../assets/img/engraving/machine.jpg";
import softwareImage from "../../assets/img/engraving/software.png";
import servoImage from "../../assets/img/engraving/servo.jpg";
import touchPanelImage from "../../assets/img/engraving/touch-panel.jpg";
import softwareDetailImage from "../../assets/img/engraving/software-detail.png";
import platformImage from "../../assets/img/engraving/platform.jpg";
import toolHeadImage from "../../assets/img/engraving/tool-head.jpg";
import dustImage from "../../assets/img/engraving/dust-extraction.jpg";
import patternImage from "../../assets/img/engraving/application-pattern.jpg";
import rhinestoneImage from "../../assets/img/engraving/application-rhinestone.jpg";
import needleImage from "../../assets/img/engraving/application-needle.jpg";
import leatherImage from "../../assets/img/engraving/application-leather.jpg";
import pvcImage from "../../assets/img/engraving/application-pvc.jpg";
import epoxyImage from "../../assets/img/engraving/application-epoxy.jpg";
import dieImage from "../../assets/img/engraving/application-die.jpg";
import cardboardImage from "../../assets/img/engraving/software.jpg";

const brochureBase = process.env.BASE_URL.replace(/\/$/, "") + "/engraving/";

export default {
  name: "EngravingMachine",
  metaInfo: {
    title: "雕刻机 · JT-1512D / JT-1812D | 川田用户社区",
    meta: [{ name: "description", content: "川田 JT-1512D、JT-1812D 垂直高速服装模板雕刻机，查看设备功能、应用范围、AI 模板设计软件及技术参数。" }],
  },
  data() {
    return {
      machineImage,
      softwareImage,
      activeSection: "overview",
      previewBrochure: null,
      videos: [],
      videoTotal: 0,
      videoLoading: false,
      videoError: "",
      videoPage: { pageNo: 1, pageSize: 8 },
      sections: [{ id: "overview", label: "产品概览" }, { id: "features", label: "功能特点" }, { id: "applications", label: "应用范围" }, { id: "software", label: "AI 模板软件" }, { id: "specifications", label: "技术参数" }, { id: "resources", label: "产品资料" }, { id: "videos", label: "视频教程" }],
      features: [
        { title: "XYZ 伺服精密驱动", description: "搭配双轨同步结构横梁，运行精准高效。", image: servoImage },
        { title: "触控操作屏", description: "直观触控操作，让设备使用更便捷。", image: touchPanelImage },
        { title: "AI 专业模板设计", description: "标配专业模板设计软件，支持一键成图与叠码。", image: softwareDetailImage },
        { title: "一体金属矩阵式吸附平台", description: "一体式金属工作平台，吸附固定加工材料。", image: platformImage },
        { title: "钻铣 / 切割 / 绘图一体化", description: "自动刀深对位，分段式刀深铣刻，完成多种加工操作。", image: toolHeadImage },
        { title: "自动吸尘系统", description: "自带自动吸尘系统，保持工作环境整洁。", image: dustImage },
      ],
      additionalFeatures: [
        { title: "一键自动对刀", description: "自动刀深对位，便捷完成对刀操作。" },
        { title: "全封闭式结构", description: "采用全封闭式结构设计，降低运行噪音。" },
        { title: "断电记忆", description: "断电或急停时，系统记录加工位置。" },
        { title: "多种连接方式", description: "支持网线、无线和 U 盘脱机连接。" },
      ],
      applications: [
        { title: "服装纸样切割", image: patternImage }, { title: "烫钻定位孔", image: rhinestoneImage },
        { title: "针稿打孔", image: needleImage }, { title: "皮革花样冲孔", image: leatherImage },
        { title: "服装 PVC 模板铣刻", image: pvcImage }, { title: "花样机环氧板切割", image: epoxyImage },
        { title: "皮具刀模纸格切割", image: dieImage }, { title: "服装纸板绘图切割", image: cardboardImage },
      ],
      softwareFeatures: [
        { title: "一键成图，模板设计秒完成", description: "选择 AI 模组与模板，通过项目生成器一键生成模板，简化设计操作。" },
        { title: "一键叠码 · 三维批量", description: "选择基础样板，按设定的尺码规则与放码参数自动生成系列尺码纸样，并进行智能叠放。" },
        { title: "一键全码展开", description: "根据放码规则与尺码范围生成完整样片，平铺于工作区，便于对比、排料和工艺审核。" },
      ],
      models: [{ name: "JT-1512D", range: "150 × 120", footprint: "233 × 185 cm（长 × 宽）" }, { name: "JT-1812D", range: "180 × 120", footprint: "253 × 185 cm（长 × 宽）" }],
      specifications: [
        { label: "工作范围", values: ["150 × 120 cm（支持定制）", "180 × 120 cm（支持定制）"] },
        { label: "模板设计软件", value: "AI 模板设计软件" },
        { label: "钻铣材料", value: "PET、PVC、FR-4（环氧板）" },
        { label: "切割材料", value: "牛皮纸、皮革、涂层布" },
        { label: "文字绘图方式", value: "钢刀绘图、油性笔绘图、笔芯绘图" },
        { label: "工作平台", value: "一体金属矩阵式吸附" },
        { label: "刀深对位", value: "自动" },
        { label: "分段刀深功能", value: "分段式刀深适应铣削（倒角场景）" },
        { label: "铣刀更换方式", value: "手动更换铣刀" },
        { label: "输出连接方式", value: "网线连接、无线连接、U 盘脱机连接" },
        { label: "碎屑清理", value: "自动吸尘" },
        { label: "水冷系统", value: "激光级冷却系统" },
        { label: "吸附能耗", value: "1500 W" },
        { label: "占地面积（长 × 宽）", values: ["233 × 185 cm", "253 × 185 cm"] },
      ],
      brochures: [
        { title: "产品概览", file: "product-overview.jpg" }, { title: "功能概述", file: "function-overview.jpg" },
        { title: "功能细节与应用范围", file: "features-applications.jpg" }, { title: "AI 软件与技术参数", file: "software-specifications.jpg" },
      ].map(item => ({ ...item, url: brochureBase + item.file })),
    };
  },
  created() {
    this.fetchVideos();
  },
  mounted() {
    window.addEventListener("scroll", this.updateActiveSection, { passive: true });
    this.$nextTick(this.updateActiveSection);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.updateActiveSection);
    if (this.previewBrochure) this.closePreview();
  },
  methods: {
    async fetchVideos(pageNo = this.videoPage.pageNo) {
      if (this.videoLoading) return;
      this.videoPage.pageNo = pageNo;
      this.videoLoading = true;
      this.videoError = "";
      try {
        const response = await this.$get("/web/video_list_gets", { ...this.videoPage, type_id: 118 }, true);
        if (!response || !Array.isArray(response.data)) throw new Error("Invalid video list response");
        this.videos = response.data;
        this.videoTotal = Number(response.totalCount) || 0;
      } catch (error) {
        this.videoError = "视频加载失败，请稍后重试。";
      } finally {
        this.videoLoading = false;
        this.$nextTick(this.updateActiveSection);
      }
    },
    changeVideoPage(pageNo) {
      if (this.videoLoading || pageNo === this.videoPage.pageNo) return;
      this.fetchVideos(pageNo);
      this.scrollToSection("videos");
    },
    videoCoverUrl(image) {
      if (/^(https?:)?\/\//i.test(image)) return image;
      return this.$imgUrl.replace(/\/$/, "") + "/" + image.replace(/^\//, "");
    },
    handleVideoCoverError(video) {
      this.$set(video, "coverFailed", true);
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (!element) return;
      const nav = this.$el.querySelector(".section-nav");
      const header = document.querySelector(".btBox");
      const offset = (header ? header.offsetHeight : 55) + (nav ? nav.offsetHeight : 60) + 16;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: Math.max(0, element.getBoundingClientRect().top + window.pageYOffset - offset), behavior: reduceMotion ? "auto" : "smooth" });
      this.activeSection = id;
    },
    updateActiveSection() {
      const nav = this.$el.querySelector(".section-nav");
      const header = document.querySelector(".btBox");
      const offset = (header ? header.offsetHeight : 55) + (nav ? nav.offsetHeight : 60) + 24;
      let current = "overview";
      this.sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top <= offset) current = section.id;
      });
      if (window.pageYOffset + window.innerHeight >= document.documentElement.scrollHeight - 2) {
        current = this.sections[this.sections.length - 1].id;
      }
      this.activeSection = current;
    },
    openBrochure(brochure) {
      this.previewTrigger = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      this.previewBrochure = brochure;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", this.handleDialogKeys);
      this.$nextTick(() => this.$refs.closeButton.focus());
    },
    closePreview() {
      this.previewBrochure = null;
      document.body.style.overflow = this.previousBodyOverflow || "";
      document.removeEventListener("keydown", this.handleDialogKeys);
      if (this.previewTrigger && this.previewTrigger.isConnected) this.previewTrigger.focus();
    },
    handleDialogKeys(event) {
      if (event.key === "Escape") this.closePreview();
      if (event.key !== "Tab" || !this.$refs.previewDialog) return;
      const controls = this.$refs.previewDialog.querySelectorAll("a[href], button");
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    },
  },
};
</script>

<style scoped>
.engraving-page { color: #252a34; font-family: "PingFang SC", "Microsoft YaHei", sans-serif; padding-bottom: 16px; }
.engraving-container { max-width: 1440px; width: calc(100% - 64px); margin: 0 auto; }
.breadcrumb { display: flex; gap: 12px; padding: 24px 0 20px; color: #858a93; font-size: 13px; }
.breadcrumb a { color: #5c626c; }
.product-hero { display: grid; grid-template-columns: 0.85fr 1.3fr; align-items: center; min-height: 480px; padding: 44px 48px; background: #fff; border: 1px solid #eceef1; border-radius: 12px; overflow: hidden; }
.eyebrow { font-size: 16px; font-weight: 600; letter-spacing: 1.4px; color: #cf232b; line-height: 1.7; }
.hero-copy h1 { margin-top: 18px; font-size: 40px; line-height: 1.4; font-weight: 700; letter-spacing: 1px; color: #202631; }
.hero-description { margin-top: 20px; color: #69717c; font-size: 15px; line-height: 1.9; }
.model-tags { display: flex; gap: 10px; margin-top: 22px; }
.model-tags span { font-size: 16px; color: #bb2029; font-weight: 600; background: #fff3f3; padding: 6px 14px; border-radius: 4px; }
.hero-actions { display: flex; gap: 12px; margin-top: 28px; }
.engraving-page button { cursor: pointer; font-family: inherit; }
.primary-button, .secondary-button { border-radius: 5px; padding: 11px 22px; font-size: 14px; line-height: 22px; border: 1px solid #d92730; }
.primary-button { color: #fff; background: #d92730; }
.primary-button:hover { background: #bb1f28; }
.primary-button span { margin-left: 14px; }
.secondary-button { color: #555e69; background: #fff; border-color: #dce0e5; }
.secondary-button:hover { border-color: #d92730; color: #d92730; }
.hero-highlights { display: flex; flex-wrap: wrap; gap: 18px; margin-top: 28px; font-size: 12px; color: #727985; }
.hero-highlights span::before { content: ""; display: inline-block; width: 4px; height: 4px; background: #d92730; vertical-align: middle; margin-right: 6px; border-radius: 50%; }
.hero-visual { min-width: 0; padding-left: 12px; }
.hero-visual img { display: block; width: 100%; height: auto; }
.visual-caption { display: flex; align-items: center; justify-content: flex-end; gap: 12px; color: #969ba3; font-size: 12px; margin-top: 12px; }
.caption-line { width: 36px; height: 1px; background: #d92730; }
.section-nav { position: sticky; top: 55px; z-index: 20; display: flex; justify-content: center; gap: 38px; min-height: 64px; margin-top: 24px; background: #fff; border: 1px solid #eceef1; border-radius: 8px; }
.section-nav button { position: relative; border: 0; background: transparent; font-size: 16px; white-space: nowrap; color: #626975; padding: 20px 6px; }
.section-nav button:hover, .section-nav button.active { color: #d92730; }
.section-nav button.active::after { content: ""; position: absolute; bottom: 0; height: 3px; border-radius: 2px; background: #d92730; left: 6px; right: 6px; }
.section-nav button.video-nav-button { align-self: center; flex-shrink: 0; min-width: 180px; padding: 12px 32px; border-radius: 5px; background: #d92730; color: #fff; font-size: 18px; font-weight: 700; line-height: 24px; letter-spacing: 2px; }
.section-nav button.video-nav-button:hover { background: #bb1f28; }
.section-nav button.video-nav-button.active::after { display: none; }
.content-section { margin-top: 54px; }
.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 26px; }
.section-heading h2 { font-size: 26px; font-weight: 600; line-height: 1.5; margin-top: 7px; color: #252a34; }
.section-heading > p { color: #828995; font-size: 13px; line-height: 1.8; padding-bottom: 3px; }
.feature-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }
.feature-card { background: #fff; border: 1px solid #eceef1; border-radius: 8px; overflow: hidden; }
.feature-card > img { display: block; width: 100%; height: auto; aspect-ratio: 2.02; object-fit: cover; }
.feature-copy { position: relative; padding: 22px 24px 25px; }
.feature-copy h3 { font-size: 17px; font-weight: 600; padding-right: 26px; }
.feature-copy p { color: #828995; font-size: 13px; line-height: 1.8; margin-top: 10px; }
.feature-number { color: #f0c7ca; font-size: 19px; position: absolute; right: 20px; top: 18px; font-weight: 600; }
.additional-features { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; margin-top: 24px; background: #f2f3f5; border-radius: 8px; padding: 28px; }
.additional-features > div { display: flex; gap: 12px; }
.additional-features > div > span { flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 23px; height: 23px; border-radius: 50%; background: #fde6e8; color: #c92b34; font-size: 13px; }
.additional-features h3 { font-size: 15px; font-weight: 600; }
.additional-features p { font-size: 12px; color: #828995; margin-top: 8px; line-height: 1.8; }
.application-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px; }
.application-card { border-radius: 8px; overflow: hidden; border: 1px solid #eceef1; background: #fff; }
.application-card > img { width: 100%; height: auto; aspect-ratio: 2.5; object-fit: cover; display: block; }
.application-card > div { display: flex; gap: 10px; align-items: center; padding: 18px; }
.application-card h3 { font-size: 14px; font-weight: 500; }
.application-card span { color: #c22c34; font-size: 12px; }
.software-section { background: #fff; border: 1px solid #eceef1; border-radius: 10px; padding: 34px 38px; }
.standard-badge { padding: 6px 12px; background: #fff3f3; border-radius: 4px; color: #c22c34; font-size: 12px; white-space: nowrap; }
.software-layout { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 48px; align-items: center; }
.software-visual { background: #f5f6f8; border-radius: 8px; padding: 22px; margin: 0; text-align: center; }
.software-visual img { display: block; width: 100%; height: auto; border: 1px solid #dce0e6; }
.software-visual figcaption { font-size: 12px; color: #8c929c; margin-top: 14px; }
.software-features article { display: flex; gap: 20px; padding: 22px 0; border-bottom: 1px solid #eef0f3; }
.software-features article:first-child { padding-top: 0; }
.software-features article:last-child { border: 0; padding-bottom: 0; }
.software-number { font-size: 23px; font-weight: 600; color: #d92730; line-height: 1.3; }
.software-features h3 { font-size: 17px; font-weight: 600; }
.software-features p { margin-top: 10px; color: #7c8390; line-height: 1.9; font-size: 13px; }
.model-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; margin-bottom: 22px; }
.model-card { position: relative; background: #fff; border: 1px solid #eceef1; border-left: 3px solid #d92730; border-radius: 8px; padding: 28px; }
.model-card > p { color: #c72a32; font-size: 17px; font-weight: 600; }
.model-card h3 { font-size: 32px; font-weight: 600; margin-top: 12px; letter-spacing: 1px; }
.model-card h3 span { font-size: 15px; font-weight: 400; color: #8b929d; }
.model-card > div { margin-top: 8px; color: #747c89; font-size: 13px; }
.model-footprint { display: block; margin-top: 18px; border-top: 1px solid #eef0f3; padding-top: 16px; font-size: 12px; color: #8b929d; }
.table-scroll { overflow-x: auto; border: 1px solid #e6e9ee; border-radius: 8px; background: #fff; }
.specification-table { width: 100%; min-width: 620px; border-collapse: collapse; font-size: 14px; line-height: 1.6; }
.specification-table th, .specification-table td { padding: 15px 24px; border-bottom: 1px solid #e9ecf0; text-align: center; }
.specification-table thead th { background: #f1f2f4; font-weight: 600; }
.specification-table thead th:not(:first-child) { color: #c42c34; }
.specification-table tbody th { width: 28%; text-align: left; background: #fafbfc; color: #626b78; font-weight: 400; }
.specification-table td { color: #444d5a; }
.specification-table tr:last-child th, .specification-table tr:last-child td { border-bottom: 0; }
.specification-note { font-size: 12px; color: #8a929e; line-height: 1.8; margin-top: 15px; }
.text-button { padding: 6px 0; border: 0; background: transparent; color: #c42c34; font-size: 13px; margin-top: 4px; }
.resource-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px; }
.resource-card { background: #fff; border: 1px solid #eceef1; border-radius: 8px; overflow: hidden; }
.brochure-preview { display: block; width: 100%; border: 0; background: #f1f2f4; padding: 20px 26px; position: relative; }
.brochure-preview img { display: block; width: 100%; height: auto; aspect-ratio: 1280 / 1739; object-fit: contain; box-shadow: 0 4px 12px #0000000d; }
.brochure-preview > span { position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%); padding: 5px 14px; font-size: 12px; white-space: nowrap; background: #ffffffeb; border: 1px solid #e6e9ee; border-radius: 4px; color: #626b78; }
.resource-copy { padding: 18px; display: flex; align-items: center; flex-wrap: wrap; justify-content: space-between; gap: 8px; }
.resource-copy h3 { font-size: 14px; font-weight: 500; }
.resource-copy a { font-size: 12px; color: #c42c34; }
.contact-panel { margin-top: 28px; background: #fff; border: 1px solid #eceef1; border-radius: 8px; padding: 30px; display: flex; justify-content: space-between; align-items: center; gap: 24px; }
.contact-panel h3 { font-size: 20px; font-weight: 600; margin: 8px 0 12px; }
.contact-panel div > p:last-child { font-size: 12px; color: #8a929e; line-height: 1.8; }
.contact-links { display: flex; gap: 32px; }
.contact-links a { color: #c72a32; }
.contact-links span { display: block; font-size: 12px; color: #8a929e; margin-bottom: 6px; }
.contact-links strong { font-size: 20px; font-weight: 600; white-space: nowrap; }
.video-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 22px; }
.video-card { min-width: 0; overflow: hidden; background: #fff; border: 1px solid #eceef1; border-radius: 8px; color: #252a34; transition: border-color 0.2s, box-shadow 0.2s; }
.video-card:hover { border-color: #e9a1a6; box-shadow: 0 6px 18px #252a340d; color: #c42c34; }
.video-cover { position: relative; aspect-ratio: 16 / 9; overflow: hidden; background: #fff1f2; }
.video-cover-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #edc1c5; font-size: 30px; font-weight: 700; letter-spacing: 4px; }
.video-cover img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: cover; }
.video-play { position: absolute; top: 50%; left: 50%; display: flex; align-items: center; justify-content: center; width: 46px; height: 46px; padding-left: 3px; border: 3px solid #fff; border-radius: 50%; background: #d92730e6; color: #fff; font-size: 17px; transform: translate(-50%, -50%); }
.video-card-body { padding: 18px; }
.video-card-body h3 { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 15px; font-weight: 600; line-height: 1.6; }
.video-card-body > span { display: block; margin-top: 12px; padding-top: 12px; border-top: 1px solid #eef0f3; font-size: 12px; color: #c42c34; }
.video-card-body > span > span { float: right; }
.video-status { min-height: 180px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 18px; border: 1px dashed #e3e6eb; border-radius: 8px; background: #fff; color: #828995; font-size: 14px; }
.video-pagination { display: flex; justify-content: center; margin-top: 26px; }
.brochure-overlay { position: fixed; inset: 0; z-index: 2000; background: #141a26b8; padding: 24px; display: flex; align-items: center; justify-content: center; }
.brochure-dialog { width: 820px; max-width: 100%; max-height: 92vh; border-radius: 8px; background: #fff; display: flex; flex-direction: column; overflow: hidden; }
.dialog-toolbar { padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; gap: 12px; border-bottom: 1px solid #eceef1; flex-shrink: 0; }
.dialog-toolbar h2 { font-size: 15px; font-weight: 600; }
.dialog-toolbar > div { display: flex; gap: 24px; align-items: center; }
.dialog-toolbar a { font-size: 13px; color: #c42c34; white-space: nowrap; }
.dialog-toolbar button { font-size: 28px; color: #69717c; border: 0; background: transparent; line-height: 30px; padding: 0 6px; }
.dialog-image { overflow: auto; padding: 16px; background: #f1f2f4; }
.dialog-image img { display: block; width: 100%; height: auto; }
.engraving-page button:focus-visible, .engraving-page a:focus-visible, .table-scroll:focus-visible { outline: 2px solid #c42c34; outline-offset: 4px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
@media (max-width: 1100px) {
  .engraving-container { width: calc(100% - 40px); }
  .product-hero { padding: 32px; min-height: 430px; }
  .hero-copy h1 { font-size: 32px; }
  .section-nav { gap: 24px; }
  .section-heading { align-items: flex-start; flex-direction: column; gap: 8px; }
  .feature-copy { padding: 20px; }
  .additional-features { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .application-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .video-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .software-layout { gap: 28px; }
  .contact-panel { align-items: flex-start; flex-direction: column; }
}
@media (max-width: 760px) {
  .engraving-container { width: calc(100% - 32px); }
  .breadcrumb { padding: 18px 0; }
  .product-hero { grid-template-columns: 1fr; padding: 28px 22px; }
  .hero-copy h1 { font-size: 31px; }
  .hero-visual { padding: 28px 0 0; }
  .section-nav { top: 96px; justify-content: flex-start; gap: 16px; padding: 0 12px; min-height: 55px; overflow-x: auto; }
  .section-nav button { padding: 16px 4px; font-size: 13px; }
  .section-nav button.video-nav-button { min-width: 148px; padding: 10px 24px; font-size: 16px; }
  .content-section { margin-top: 36px; }
  .section-heading h2 { font-size: 22px; }
  .feature-grid, .application-grid, .resource-grid, .video-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  .feature-copy { padding: 16px 14px; }
  .feature-copy h3 { font-size: 15px; padding-right: 0; }
  .feature-number { display: none; }
  .feature-copy p { font-size: 12px; }
  .additional-features { padding: 22px 16px; gap: 22px 14px; }
  .application-card > div { padding: 14px 12px; gap: 8px; align-items: flex-start; }
  .application-card h3 { font-size: 13px; }
  .software-section { padding: 24px 20px; }
  .software-layout { grid-template-columns: 1fr; gap: 24px; }
  .software-visual { max-width: 480px; margin: 0 auto; }
  .software-features h3 { font-size: 16px; }
  .model-grid { gap: 14px; }
  .model-card { padding: 20px 15px; }
  .model-card h3 { font-size: 23px; letter-spacing: 0; }
  .model-card > p { font-size: 15px; }
  .specification-table th, .specification-table td { padding: 13px 16px; }
  .brochure-preview { padding: 16px; }
  .resource-copy { padding: 14px; }
  .video-card-body { padding: 14px; }
  .contact-panel { padding: 24px 20px; }
  .contact-links { gap: 24px; flex-wrap: wrap; }
  .contact-links strong { font-size: 17px; }
  .brochure-overlay { padding: 12px; }
  .dialog-toolbar { padding: 12px 14px; }
  .dialog-toolbar > div { gap: 12px; }
  .dialog-image { padding: 8px; }
}
@media (max-width: 380px) {
  .feature-grid, .model-grid, .video-grid { grid-template-columns: 1fr; }
  .hero-actions { gap: 8px; }
  .primary-button, .secondary-button { padding: 10px 14px; }
  .additional-features { grid-template-columns: 1fr; }
}
</style>
